import * as React from 'react'
import i18next from 'i18next'
import { RemoteResource } from '../../services/api'
import { GalleryScrollTarget, useJustifiedGallery } from './JustifiedGallery.hook'
import { galleryMetrics, GalleryLayoutMode } from './buildSections'
import { formatDayHeader } from './formatDayHeader'
import { PhotoTile } from './PhotoTile.component'
import { VisibleRange } from './visibleRange'

/** invisible per-row overflow room for the tile hover scale + shadow */
const HOVER_BLEED = 16

export interface JustifiedGalleryProps {
  photos: RemoteResource[]
  onPhotoClick?: (photoId: string) => void
  groupByDate?: boolean
  layoutMode?: GalleryLayoutMode
  onVisibleRangeChange?: (range: VisibleRange) => void
  scrollTarget?: GalleryScrollTarget | null
  onScrollTargetApplied?: () => void
}

export const JustifiedGallery: React.FC<JustifiedGalleryProps> = ({
  photos,
  onPhotoClick,
  groupByDate = true,
  layoutMode = 'justified',
  onVisibleRangeChange,
  scrollTarget,
  onScrollTargetApplied,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { layout } = useJustifiedGallery({
    photos,
    containerRef,
    groupByDate,
    layoutMode,
    onVisibleRangeChange,
    scrollTarget,
    onScrollTargetApplied,
  })

  return (
    <div ref={containerRef} className="w-full" style={{ overflowAnchor: 'none' }}>
      {layout.sections.map((section) => (
        // flex column prevents the rows' vertical margins from collapsing,
        // which would stretch the bleed compensation and widen row gaps
        <div key={`${section.key ?? 'undated'}-${section.startIndex}`} className="flex flex-col">
          {section.key ? (
            <h2
              className="flex items-end pb-2 text-sm font-medium text-gray-800"
              style={{ height: galleryMetrics.headerHeight }}
            >
              {formatDayHeader(section.key, i18next.language)}
            </h2>
          ) : (
            <div style={{ height: galleryMetrics.headerlessGap }} />
          )}
          {layout.layoutMode === 'masonry' ? (
            // marginBottom carries the trailing section gap the flex rows get via row margins
            <div className="relative" style={{ height: section.contentHeight, marginBottom: layout.gap }}>
              {section.rows.map((row) => (
                <div
                  key={row.tiles[0].item.id}
                  className="absolute"
                  style={{
                    left: row.tiles[0].left,
                    top: row.top - (section.top + (section.key ? galleryMetrics.headerHeight : galleryMetrics.headerlessGap)),
                    width: row.tiles[0].width,
                    height: row.height,
                  }}
                >
                  <PhotoTile
                    photo={row.tiles[0].item}
                    width={row.tiles[0].width}
                    height={row.height}
                    onPhotoClick={onPhotoClick}
                  />
                </div>
              ))}
            </div>
          ) : (
            section.rows.map((row) => (
              <div
                key={row.tiles[0].item.id}
                className="flex"
                // content-visibility paint-clips at the row box; the padding/negative-margin
                // bleed keeps the hover scale + shadow inside it without shifting layout
                style={{
                  height: row.height,
                  boxSizing: 'content-box',
                  padding: HOVER_BLEED,
                  margin: -HOVER_BLEED,
                  marginBottom: layout.gap - HOVER_BLEED,
                  columnGap: layout.gap,
                  contentVisibility: 'auto',
                  containIntrinsicSize: `1px ${row.height + HOVER_BLEED * 2}px`,
                }}
              >
                {row.tiles.map((tile) => (
                  <PhotoTile
                    key={tile.item.id}
                    photo={tile.item}
                    width={tile.width}
                    height={tile.height}
                    onPhotoClick={onPhotoClick}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      ))}
    </div>
  )
}
